// src/features/players/components/PlayerTable.tsx

import { useCallback, useMemo } from 'react';
import { Table, Checkbox, Text, Stack, ThemeIcon } from '@mantine/core';
import { IconPlayerPlay } from '@tabler/icons-react';
import { useSelection } from '@/shared/contexts/SelectionContext';
import { PlayerTableRow } from './PlayerTableRow';
import type { PlayerGridRow } from '@/lib/types/Player';
import type { PlayerSortKey } from '@/lib/constants/player';

type SortDirection = 'asc' | 'desc';

interface PlayerTableProps {
  rows: PlayerGridRow[];
  sortKey: PlayerSortKey;
  sortDirection: SortDirection;
  onSort: (key: PlayerSortKey) => void;
  onCopy: (row: PlayerGridRow) => void;
  onActivateToggle: (row: PlayerGridRow) => void;
  onDelete: (row: PlayerGridRow) => void;
}

interface SortableHeaderProps {
  label: string;
  sortKey: PlayerSortKey;
  currentSortKey: PlayerSortKey;
  currentSortDirection: SortDirection;
  onSort: (key: PlayerSortKey) => void;
  w?: number;
}

function SortableHeader({ label, sortKey, currentSortKey, currentSortDirection, onSort, w }: SortableHeaderProps) {
  const isActive = currentSortKey === sortKey;
  const arrow = isActive ? (currentSortDirection === 'asc' ? ' \u2191' : ' \u2193') : '';

  return (
    <Table.Th
      w={w}
      style={{ cursor: 'pointer', userSelect: 'none' }}
      onClick={() => onSort(sortKey)}
    >
      <Text size="sm" fw={600} c={isActive ? undefined : 'dimmed'}>
        {label}{arrow}
      </Text>
    </Table.Th>
  );
}

export function PlayerTable({
  rows,
  sortKey,
  sortDirection,
  onSort,
  onCopy,
  onActivateToggle,
  onDelete,
}: PlayerTableProps) {
  const { selectedIds, selectAll, clearSelection } = useSelection();

  const allIds = useMemo(() => rows.map(r => r.playerId), [rows]);

  const allSelected = rows.length > 0 && selectedIds.size === rows.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      clearSelection();
    } else {
      selectAll(allIds);
    }
  }, [allSelected, allIds, selectAll, clearSelection]);

  if (rows.length === 0) {
    return (
      <Stack align="center" justify="center" py={80} gap="md">
        <ThemeIcon size={64} radius="xl" variant="light" color="gray">
          <IconPlayerPlay size={32} />
        </ThemeIcon>
        <Text size="lg" fw={500} c="dimmed">No players found</Text>
        <Text size="sm" c="dimmed">Create your first player to get started.</Text>
      </Stack>
    );
  }

  return (
    <Table highlightOnHover verticalSpacing="sm" withTableBorder>
      <Table.Thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--mantine-color-gray-0)', zIndex: 1 }}>
        <Table.Tr>
          <Table.Th w={48}>
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={handleSelectAll}
              aria-label="Select all"
            />
          </Table.Th>
          <SortableHeader label="Name" sortKey="label" currentSortKey={sortKey} currentSortDirection={sortDirection} onSort={onSort} />
          <SortableHeader label="Status" sortKey="enabled" currentSortKey={sortKey} currentSortDirection={sortDirection} onSort={onSort} w={100} />
          <Table.Th w={60} ta="center">
            <Text size="sm" fw={600} c="dimmed">Default</Text>
          </Table.Th>
          <SortableHeader label="Created" sortKey="created" currentSortKey={sortKey} currentSortDirection={sortDirection} onSort={onSort} w={120} />
          <SortableHeader label="Updated" sortKey="updated" currentSortKey={sortKey} currentSortDirection={sortDirection} onSort={onSort} w={120} />
          <Table.Th w={48} />
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map((row, index) => (
          <PlayerTableRow
            key={row.playerId}
            row={row}
            index={index}
            allIds={allIds}
            onCopy={onCopy}
            onActivateToggle={onActivateToggle}
            onDelete={onDelete}
          />
        ))}
      </Table.Tbody>
    </Table>
  );
}
